import Badge from "@/common/components/Badge";
import Box from "@/common/components/Box";
import { FileBox } from "lucide-react";
import { Control, Controller, FieldErrors, useWatch } from "react-hook-form";
import { CompanyBranchValidationSchema } from "../../schemas/createBranch.schema";
import { CurrencyTextField } from "@/common/components/CurrencyTextField";
import { ClientCompanyAllowedCategoryAndProductData } from '../../types/allowedCategoriesAndProducts.type';
import { formatCurrency, formatDecimalUnit } from '@/common/utils/formatters';
import { unFormatCurrency, unFormatUnknownUnit } from "@/common/utils/unFormatters";
import { ControlledByType } from "../../types/companyGovernmentAllocation.type";
import { UnitTextField } from "@/common/components/UnitTextField";
import { unities, unitiesDecimals } from "@/common/constants/unities.constant";

interface CreditProductConfigProps {
  displayName: string;
  unitForQuantity?: string;
  categoryIndex: number;
  productIndex: number;
  allowedProductSummary: ClientCompanyAllowedCategoryAndProductData;
  removeProduct: () => void;
  control: Control<CompanyBranchValidationSchema>;
  errors: FieldErrors<CompanyBranchValidationSchema>;
  disabled?: boolean;
  companyKind?: string;
  controlledBy?: ControlledByType;
}

export const CreditProductConfig = ({
  displayName,
  unitForQuantity,
  categoryIndex,
  productIndex,
  allowedProductSummary,
  removeProduct,
  control,
  errors,
  disabled,
  companyKind,
  controlledBy = 'currency',
}: CreditProductConfigProps) => {
  const isQuantity = controlledBy === 'quantity';

  const watchProductCategoryGrantedCredit = useWatch({
    control,
    name: `product_category_granted_credits_attributes.${categoryIndex}`,
  });

  const watchCurrentProductGrantedCredit = useWatch({
    control,
    name: `product_category_granted_credits_attributes.${categoryIndex}.product_granted_credits_attributes.${productIndex}.granted_credit`,
  });

  const watchCurrentProductGrantedQuantity = useWatch({
    control,
    name: `product_category_granted_credits_attributes.${categoryIndex}.product_granted_credits_attributes.${productIndex}.granted_quantity` as `product_category_granted_credits_attributes.${number}.product_granted_credits_attributes.${number}.granted_credit`,
  });

  const productGrantedCreditsSum = watchProductCategoryGrantedCredit?.product_granted_credits_attributes?.reduce((sum, product, index) => {
    if (index !== productIndex) {
      if (isQuantity) {
        const grantedQuantity = unFormatUnknownUnit(product.granted_quantity || '0');
        return sum + grantedQuantity || 0;
      }
      return sum + (unFormatCurrency(product.granted_credit || '') || 0);
    }

    return sum;
  }, 0) || 0;

  const allowedProductAvailableTotal = isQuantity
    ? (Number(allowedProductSummary?.available_quantity) || 0)
    : (Number(allowedProductSummary?.available_total) || 0);

  let productCategoryGrantedCredit: number;
  if (isQuantity) {
    const grantedQuantity = unFormatUnknownUnit(watchProductCategoryGrantedCredit.granted_quantity || '0');
    productCategoryGrantedCredit = Number(grantedQuantity) || 0;
  } else {
    productCategoryGrantedCredit = watchProductCategoryGrantedCredit.granted_credit ? unFormatCurrency(watchProductCategoryGrantedCredit.granted_credit) : 0;
  }
  const productCategoryGrantedCreditLeft = productCategoryGrantedCredit - productGrantedCreditsSum;

  const availableTotal = allowedProductAvailableTotal
    ? Math.min(allowedProductAvailableTotal, Math.max(productCategoryGrantedCreditLeft, 0))
    : Math.max(productCategoryGrantedCreditLeft, 0);

  const availableForAllocation = isQuantity
    ? Math.max(
      (unFormatUnknownUnit((watchProductCategoryGrantedCredit as unknown as { granted_quantity?: string }).granted_quantity || '0') || 0) -
      (unFormatUnknownUnit(watchCurrentProductGrantedQuantity || '0') || 0),
      0,
    )
    : Math.max(
      unFormatCurrency(watchProductCategoryGrantedCredit.granted_credit || '') -
      unFormatCurrency(watchCurrentProductGrantedCredit || ''),
      0,
    );

  return (
    <Box className="flex flex-col gap-4">
      <Badge
        Icon={FileBox}
        text={displayName || ""}
        onDelete={() => removeProduct()}
        className="rounded-sm flex bg-neutral-op-8 w-fit flex-row-reverse overflow-hidden !p-0 gap-0"
        overwriteClassName
        deleteClassName="bg-black p-2"
        contentClassName="px-3 py-2"
        deleteIconColor="var(--foreground)"
        disabled={disabled}
      />

      <div className='flex flex-col gap-1'>
        {isQuantity ? (
          <Controller
            name={`product_category_granted_credits_attributes.${categoryIndex}.product_granted_credits_attributes.${productIndex}.granted_quantity` as `product_category_granted_credits_attributes.${number}.product_granted_credits_attributes.${number}.granted_credit`}
            control={control}
            disabled={disabled}
            render={({ field }) => (
              <UnitTextField
                decimals={unitForQuantity ? unitiesDecimals[unitForQuantity] : 2}
                max={999_999_999.99}
                unit={unitForQuantity ? unities[unitForQuantity] : undefined}
                label="Quantidade total"
                markAsRequired
                errorMessage={(errors.product_category_granted_credits_attributes?.[categoryIndex]?.product_granted_credits_attributes?.[productIndex] as { granted_quantity?: { message?: string } })?.granted_quantity?.message || " "}
                {...field}
              />
            )}
          />
        ) : (
          <Controller
            name={`product_category_granted_credits_attributes.${categoryIndex}.product_granted_credits_attributes.${productIndex}.granted_credit`}
            control={control}
            disabled={disabled}
            render={({ field }) => (
              <CurrencyTextField
                label="Valor total"
                markAsRequired
                errorMessage={errors.product_category_granted_credits_attributes?.[categoryIndex]?.product_granted_credits_attributes?.[productIndex]?.granted_credit?.message || " "}
                {...field}
              />
            )}
          />
        )}

        {allowedProductSummary && (
          <div className='flex gap-3'>
            <p className='italic text-xs text-white-400'>
              {isQuantity ? 'Quantidade possível: ' : 'Total possível: '}
              <span data-testid={`available_total_category_${categoryIndex}_product_${productIndex}`}>
                {isQuantity ? formatDecimalUnit(Number(availableTotal), unitForQuantity) : formatCurrency(Number(availableTotal) || 0)}
              </span>
            </p>

            <p className='italic text-xs text-white-400'>
              Disponível para distribuir: {isQuantity ? formatDecimalUnit(Number(availableForAllocation), unitForQuantity) : formatCurrency(availableForAllocation)}
            </p>

            {companyKind !== 'private_company' && (
              <p className='italic text-xs text-white-400'>
                {isQuantity ? 'Quantidade alocada: ' : 'Total alocado: '}
                {isQuantity
                  ? (allowedProductSummary?.allocated_quantity ? formatDecimalUnit(Number(allowedProductSummary?.allocated_quantity), unitForQuantity) : '--')
                  : (allowedProductSummary?.allocated_total ? formatCurrency(Number(allowedProductSummary?.allocated_total) || 0) : '--')
                }
              </p>
            )}

            <p className='italic text-xs text-white-400'>
              {isQuantity ? 'Quantidade distribuída: ' : 'Total distribuído: '}
              {isQuantity
                ? (allowedProductSummary?.distributed_quantity ? formatDecimalUnit(Number(allowedProductSummary?.distributed_quantity), unitForQuantity) : '--')
                : (allowedProductSummary?.distributed_total ? formatCurrency(Number(allowedProductSummary?.distributed_total) || 0) : '--')
              }
            </p>
          </div>
        )}
      </div>
    </Box>
  )
}
